import * as Yup from 'yup';

import { billingTypeOptions, isInvoiceBillingType } from '~constants/maps';

import { deriveRowServiceDetail } from '~support-at-home/lib/derive';

import { Column, ColumnType, Context, Row } from '~components/table-view';
import { selectEditor } from '~components/table-view/columnEditors';
import ReadOnlyCategory from '~support-at-home/components/config/price-plans/ReadOnlyCategory';
import ServiceTypeCell from '~support-at-home/components/config/price-plans/ServiceTypeCell';

import { Lookup, SahCategory } from '~lookups/models';

import { PricePlanModalRow, SahServiceTypeAssociation } from '~support-at-home/models';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

// Six rate columns compete with three descriptive ones, so every column but Service Type is
// fixed and Service Type takes what is left.
//
// Each width is sized to the widest thing the column must show WITHOUT truncating, measured with
// the column rendered as an editable control - a new row shows selects where a saved row shows
// text, and the select is always the wider case once its 34px dropdown indicator and the value
// container's 8px of padding are counted.
//
//   Category  - "Clinical Support" and "Everyday Living" both measure 113px; at 130px the select
//               truncated to "Clinical S...".
//   Billing   - fits its longest option plus the caret; at the previous auto width "Hourly"
//               truncated to "Ho...".
//   Rate      - fits a five-figure hourly rate ("12,345.67") beside the trimmed `$` prefix the
//               minimal density gives it.
const CATEGORY_PX = 160;
const BILLING_TYPE_PX = 130;
const RATE_PX = 105;
const ACTIONS_PX = 76;
// The narrowest Service Type may be squeezed to before the table starts scrolling instead. Below
// roughly this, a name like "SAH - Assistance with Self-Administration of Medication" stops
// wrapping between words and starts breaking mid-word over six or seven lines.
const SERVICE_TYPE_MIN_PX = 240;

const CATEGORY_WIDTH = `${CATEGORY_PX}px`;
const BILLING_TYPE_WIDTH = `${BILLING_TYPE_PX}px`;
const RATE_WIDTH = `${RATE_PX}px`;

/**
 * The width below which this table should scroll rather than keep compressing.
 *
 * Derived from the same constants the columns use, and from the shift count, because the rate
 * columns are generated one per shift - a company with more shifts needs a wider floor, and a
 * hardcoded number would be wrong for them.
 *
 * @param shiftCount - how many shift rate columns the table is rendering
 */
export const minTableWidth = (shiftCount: number): number =>
  CATEGORY_PX + SERVICE_TYPE_MIN_PX + BILLING_TYPE_PX + RATE_PX * shiftCount + ACTIONS_PX;

const isEditable = (row: Row, context?: Context) => {
  // blanket check for all applicable columns when editing
  if (!context?.initialRows) return true;
  const rows = context.initialRows as PricePlanModalRow[];
  return !rows.find(({ id }) => id === row.id);
};

export const columnsFactory = (
  categories: SahCategory[],
  serviceTypes: ServiceTypeRow[],
  serviceTypeAssocs: SahServiceTypeAssociation[],
  shifts: Lookup[],
): Column[] => {
  const categoryOptions = categories.map(({ id, name }) => ({ id, name }));
  const billingOptions = billingTypeOptions.map(({ value, label }) => ({ id: value, name: label }));

  const onCategoryChange = (row: any, context: any) => {
    const { id, category_id, service_type_id } = row;
    const validServiceTypes = getServiceTypesForCategory(
      id,
      category_id,
      serviceTypeAssocs,
      serviceTypes,
      context?.getAllRows?.() ?? [],
    );

    if (!validServiceTypes.some((st) => st.id === service_type_id)) {
      return { service_type_id: undefined };
    }

    return { service_type_id };
  };

  const getServiceTypesForCategory = (
    id: string | number,
    category_id: string,
    serviceTypeAssocs: SahServiceTypeAssociation[],
    serviceTypes: ServiceTypeRow[],
    allRows: any,
  ) => {
    const serviceTypeIds = serviceTypeAssocs
      .filter((assoc) => assoc.category_id === category_id)
      .flatMap((assoc) => assoc.service_type_ids);

    const allocatedServiceTypeIds = allRows
      .filter((row: any) => row.category_id === category_id && row.id !== id)
      .map((row: any) => row.service_type_id);

    return serviceTypes.filter(({ id }) => serviceTypeIds.includes(id) && !allocatedServiceTypeIds.includes(id));
  };

  const clearShiftValues = (row: any, shifts: Lookup[]) => {
    if (isInvoiceBillingType(row.billing_type)) {
      const clearedShifts: Record<string, any> = {};

      shifts.forEach(({ id }) => {
        clearedShifts[id] = undefined;
      });

      return clearedShifts;
    }
    return {};
  };

  const columns: Column[] = [
    {
      id: 'category_id',
      header: 'Category',
      type: 'select',
      style: { width: CATEGORY_WIDTH },
      // Category is the one select here that must stay on a single line: its labels are short and
      // the column above is sized to hold the longest outright, so wrapping would only cost row
      // height. Billing Type deliberately does wrap - "Invoice + 10%" is unreadable truncated.
      classNames: { cell: 'nowrap-value' },
      options: categoryOptions,
      onChange: onCategoryChange,
      isEditable,
      renderReadOnly: (_value, row) => ReadOnlyCategory({ row, serviceTypeAssocs, categories }),
      validation: Yup.string().required('Category is required'),
    },
    {
      id: 'service_type_id',
      header: 'Service Type',
      type: 'select',
      // The only fluid column: no fixed width, so it absorbs whatever the others leave and wraps
      // rather than truncating. Its floor cannot live here - `table-layout: fixed` ignores
      // `min-width` on a cell - so it is enforced through the table's own minimum width, see
      // `minTableWidth` above.
      classNames: { cell: 'price-plan-service-type-cell' },
      options: serviceTypes,
      isEditable,
      renderEditor: selectEditor(
        (row, context) =>
          getServiceTypesForCategory(
            row.id,
            row.category_id,
            serviceTypeAssocs,
            serviceTypes,
            context?.getAllRows?.() ?? [],
          ),
        true,
      ),
      renderReadOnly: (_value, row) =>
        ServiceTypeCell({ row, serviceTypes, serviceDetail: deriveRowServiceDetail(row, serviceTypeAssocs) }),
      validation: Yup.number().required('Service Type is required'),
    },
    {
      id: 'billing_type',
      header: 'Billing Type',
      type: 'select',
      style: { width: BILLING_TYPE_WIDTH },
      options: billingOptions,
      onChange: (row) => clearShiftValues(row, shifts),
      validation: Yup.string().required('Billing Type is required'),
    },
    ...shifts.map(({ id, name }) => ({
      id,
      header: name,
      type: 'currency' as ColumnType,
      style: { width: RATE_WIDTH },
      isEditable: (row: Row) => row.billing_type && !isInvoiceBillingType(row.billing_type),
      validation: Yup.number().typeError('Cost must be a number').min(0, 'Cost cannot be negative').nullable(),
    })),
  ];

  return columns;
};
